import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, LogIn } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1708397440521-e28f1ac020a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxzYWx2YWRvciUyMGJhaGlhJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NjYyOTE4NHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, errMsg } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src={BG} alt="Salvador" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#00284f]/85 mix-blend-multiply" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 border border-white/40 grid place-items-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <span className="text-xs tracking-[0.28em] uppercase">e-Salvador</span>
          </div>
          <div className="max-w-lg">
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-5">Gabinete · Controle Interno</p>
            <h1 className="text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
              Controle de Processos
              <span className="block text-white/50">do e-Salvador</span>
            </h1>
            <p className="mt-6 text-sm text-white/70 leading-relaxed">
              Rastreamento de tramitações, sindicâncias e auditoria completa de acessos e
              modificações. Acesso restrito a usuários cadastrados pelo master.
            </p>
          </div>
          <p className="text-[11px] text-white/40 mono">
            Desenvolvido por Adenilton Silva.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span className="text-xs tracking-[0.25em] uppercase text-primary">e-Salvador</span>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Acesso ao sistema</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-8">
            Informe o usuário e a senha cadastrada.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-wider text-slate-500">
                Usuário
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="mt-1.5 h-11 mono"
                placeholder="usuario"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
                Senha
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5 h-11"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {error && (
            <p
              data-testid="login-error"
              className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="mt-7 w-full h-11 bg-primary hover:bg-[#003366] transition-colors duration-150"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
            {busy ? "" : "Entrar"}
          </Button>

          <p className="mt-8 text-[11px] text-slate-400 leading-relaxed">
            Todos os acessos e modificações são registrados na trilha de auditoria.
          </p>
        </form>
      </div>
    </div>
  );
}
